import { useMemo } from 'react';
import { makeStyles, tokens, Text } from '@fluentui/react-components';

/**
 * PromptDiff — client-side line-level diff between two prompt templates.
 *
 * Uses a classic LCS DP to produce a unified diff (added / removed / context).
 * No third-party diff lib: the templates are short enough (< 200 lines in
 * practice) that O(n*m) is fine, and we avoid Fluent UI theming conflicts.
 */

type DiffOp = 'same' | 'add' | 'del';
export interface DiffLine {
  op: DiffOp;
  leftNo: number | null;   // line number in `left` (null if inserted)
  rightNo: number | null;  // line number in `right` (null if deleted)
  text: string;
}

export function computeLineDiff(left: string, right: string): DiffLine[] {
  const a = left.split('\n');
  const b = right.split('\n');
  const n = a.length, m = b.length;

  // LCS length table
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  // Backtrack to build the unified diff
  const out: DiffLine[] = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ op: 'same', leftNo: i + 1, rightNo: j + 1, text: a[i] });
      i++; j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ op: 'del', leftNo: i + 1, rightNo: null, text: a[i] });
      i++;
    } else {
      out.push({ op: 'add', leftNo: null, rightNo: j + 1, text: b[j] });
      j++;
    }
  }
  while (i < n) { out.push({ op: 'del', leftNo: i + 1, rightNo: null, text: a[i] }); i++; }
  while (j < m) { out.push({ op: 'add', leftNo: null, rightNo: j + 1, text: b[j] }); j++; }
  return out;
}

export interface DiffStats { additions: number; deletions: number; unchanged: number; }

export function summarizeDiff(lines: DiffLine[]): DiffStats {
  let additions = 0, deletions = 0, unchanged = 0;
  for (const l of lines) {
    if (l.op === 'add') additions++;
    else if (l.op === 'del') deletions++;
    else unchanged++;
  }
  return { additions, deletions, unchanged };
}

const useStyles = makeStyles({
  wrap: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '6px',
    background: tokens.colorNeutralBackground3,
    overflow: 'auto',
    maxHeight: '420px',
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: '12px',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '44px 44px 16px 1fr',
    columnGap: '8px',
    padding: '1px 8px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  gutter: {
    color: tokens.colorNeutralForeground3,
    textAlign: 'right',
    userSelect: 'none',
  },
  marker: { userSelect: 'none' },
  add: { background: '#e6ffed', color: '#22863a' },
  del: { background: '#ffeef0', color: '#b31d28' },
  same: { color: tokens.colorNeutralForeground1 },
  stats: {
    display: 'flex',
    gap: '12px',
    marginBottom: '8px',
    fontFamily: tokens.fontFamilyMonospace,
  },
  addBadge: { color: '#22863a', fontWeight: 600 },
  delBadge: { color: '#b31d28', fontWeight: 600 },
  empty: {
    padding: '16px',
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
});

export interface PromptDiffProps {
  left: string;
  right: string;
  leftLabel?: string;
  rightLabel?: string;
}

export default function PromptDiff({ left, right, leftLabel, rightLabel }: PromptDiffProps) {
  const s = useStyles();
  const lines = useMemo(() => computeLineDiff(left, right), [left, right]);
  const stats = useMemo(() => summarizeDiff(lines), [lines]);

  const identical = stats.additions === 0 && stats.deletions === 0;

  return (
    <div>
      <div className={s.stats}>
        <Text size={200}>
          <span className={s.delBadge}>−{stats.deletions}</span>{' '}
          <span className={s.addBadge}>+{stats.additions}</span>
        </Text>
        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
          {leftLabel ?? 'left'} → {rightLabel ?? 'right'}
        </Text>
      </div>
      <div className={s.wrap}>
        {identical && <div className={s.empty}>No differences.</div>}
        {!identical && lines.map((l, idx) => {
          const cls = l.op === 'add' ? s.add : l.op === 'del' ? s.del : s.same;
          const marker = l.op === 'add' ? '+' : l.op === 'del' ? '−' : ' ';
          return (
            <div key={idx} className={`${s.row} ${cls}`}>
              <span className={s.gutter}>{l.leftNo ?? ''}</span>
              <span className={s.gutter}>{l.rightNo ?? ''}</span>
              <span className={s.marker}>{marker}</span>
              <span>{l.text}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
